import type { RecognizedDocument } from "@/api/schemas";

/**
 * G1 — dokumenti čestice (ZK izvadak) u modulu gradivosti.
 *
 * Čiste funkcije (obrazac paketa 2-5, testabilno bez rendera/mreže):
 * izvlače činjenice iz ZK ekstrakcije (`document_recognizer` ih već daje
 * točno — vidi G1_PLAN_IZVEDBE) i slažu cross-check nalaze protiv G0
 * auto-zone. Ekstrakcija se NE ponavlja ovdje; ovo je potrošač.
 */

/** Normalizirane činjenice iz ZK/katastarskog izvatka. Vlasnici dolaze
 *  kao liste (više suvlasnika) — čuvamo ih kao liste. */
export interface ZkFacts {
  kc: string | null;
  ko: string | null;
  povrsinaM2: number | null;
  nacinUporabe: string | null;
  tereti: string | null;
  vlasnici: string[];
}

function firstString(v: unknown): string | null {
  if (typeof v === "string") return v.trim() || null;
  if (Array.isArray(v)) {
    const s = v.find((x) => typeof x === "string" && x.trim());
    return typeof s === "string" ? s.trim() : null;
  }
  return null;
}

function asStringList(v: unknown): string[] {
  if (typeof v === "string") return v.trim() ? [v.trim()] : [];
  if (Array.isArray(v)) {
    return v.filter((x): x is string => typeof x === "string" && x.trim().length > 0).map((x) => x.trim());
  }
  return [];
}

function toNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/m²|m2/gi, "").replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** Value out of a recognized field (unwraps ``{value, confidence}``). */
function fieldValue(doc: RecognizedDocument, key: string): unknown {
  const f = (doc.fields ?? {})[key];
  if (f && typeof f === "object" && "value" in f) return (f as { value: unknown }).value;
  return f ?? null;
}

/** Only ZK/katastar extracts carry these facts. Returns null otherwise so
 *  the caller can honestly say "this isn't a parcel document". */
export function extractZkFacts(doc: RecognizedDocument): ZkFacts | null {
  if (doc.document_type !== "katastar_vlasnicki") return null;
  return {
    kc: firstString(fieldValue(doc, "katastarska_cestica")),
    ko: firstString(fieldValue(doc, "katastarska_opcina")),
    povrsinaM2: toNumber(fieldValue(doc, "povrsina_cestice")),
    nacinUporabe: firstString(fieldValue(doc, "nacin_uporabe")),
    tereti: firstString(fieldValue(doc, "tereti")),
    vlasnici: asStringList(fieldValue(doc, "vlasnik_naziv")),
  };
}

/** Deterministički sastavljen upit za postojeći chat/parse put — iz čistih
 *  k.č./k.o. (ne slobodan tekst), pa parse.py ga love regexom kao da ga je
 *  korisnik utipkao. Bez k.č. → null (nema što očitati). */
export function zkChatText(facts: ZkFacts): string | null {
  if (!facts.kc) return null;
  return facts.ko ? `k.č. ${facts.kc} k.o. ${facts.ko}` : `k.č. ${facts.kc}`;
}

export interface ParcelDocCheck {
  kind: "ok" | "warn" | "info";
  text: string;
}

/** Katastarske uporabe koje su poljoprivredne — signal, NE prostorna
 *  namjena (vinograd u katastru može ležati u građevinskoj zoni). */
const AGRI_USE_RE = /vinograd|oranic|pašnjak|pasnjak|voćnjak|vocnjak|livad|šuma|suma|maslinik|vrt\b/i;

/**
 * Cross-check ZK činjenica protiv G0 auto-zone (mandat: „neslaganje =
 * NALAZ, nikad tiho"). Sve je deterministički iz podataka koje već imamo.
 */
export function parcelDocChecks(
  facts: ZkFacts,
  zoneParcelAreaM2: number | null,
): ParcelDocCheck[] {
  const out: ParcelDocCheck[] = [];

  // Površina: ZK vs katastar (G0). Poklapanje potvrđuje; razlika > 3 % je nalaz.
  if (facts.povrsinaM2 != null && zoneParcelAreaM2 != null) {
    const diff = Math.abs(facts.povrsinaM2 - zoneParcelAreaM2);
    const rel = diff / zoneParcelAreaM2;
    if (rel > 0.03) {
      out.push({
        kind: "warn",
        text:
          `Površina iz ZK (${facts.povrsinaM2.toLocaleString("hr-HR")} m²) razlikuje se od ` +
          `katastarske (${zoneParcelAreaM2.toLocaleString("hr-HR")} m²) — provjeri koji je izvor mjerodavan.`,
      });
    } else {
      out.push({
        kind: "ok",
        text: `Površina ${facts.povrsinaM2.toLocaleString("hr-HR")} m² potvrđena (ZK i katastar se slažu).`,
      });
    }
  }

  // Teret → readiness upozorenje (utječe na smještaj građevine).
  if (facts.tereti) {
    out.push({
      kind: "warn",
      text: `Čestica ima teret upisan u ZK (${facts.tereti}) — može ograničiti smještaj građevine; provjeri koridor/uvjete prije projektiranja.`,
    });
  }

  // Katastarska uporaba poljoprivredna → informativan nalaz (nije namjena).
  if (facts.nacinUporabe && AGRI_USE_RE.test(facts.nacinUporabe)) {
    out.push({
      kind: "info",
      text:
        `Katastarska uporaba je poljoprivredna (${facts.nacinUporabe}) — to je katastarski podatak, ` +
        `ne prostorna namjena; provjeri dopušta li plan gradnju u zoni čestice.`,
    });
  }

  return out;
}

/** Čitljiv sažetak vlasnika za karticu „iz dokumenta" — nikad array u polje. */
export function ownersSummary(vlasnici: string[]): string | null {
  if (vlasnici.length === 0) return null;
  if (vlasnici.length === 1) return vlasnici[0];
  return `${vlasnici.join("; ")} (${vlasnici.length} suvlasnika)`;
}
